import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { useTypedDispatch } from '@hook/common/useStore';
import { MEDIUM_MODAL_WIDTH } from '@constant/size';
import { IWorkbookQuitModalData } from '@datatype/iyagigo/type';
import {
  IYAGIGO_PATH,
  createSpeakingPath,
  createClassroomPath,
  createClipPath,
  createClozePath,
  createHubPath,
  createStudioPath,
  createTestPath,
  createVocabularyBookPath,
} from '@constant/iyagigo/route-path';
import { useNavigate } from 'react-router-dom';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';

interface IProps {
  data: IWorkbookQuitModalData['data'];
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${MEDIUM_MODAL_WIDTH};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const DIALOG_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
} as const;

const WorkbookQuitModal: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { mainMenu, subMenu, nextPath } = data;
  const handleQuitClick = () => {
    dispatch(modalActions.hideModal());
    dispatch(workbookActions.storeIsStudying(false));
    dispatch(classroomActions.storeIsStudying(false));

    // subMenu가 있으면 그 탭으로 전환, 없으면 reducer에서 default subMenu로 자동 fallback
    // nextPath가 명시되어 있으면 mainMenu home 대신 그 경로로 이동 (예: cloze-note 복귀)
    if (mainMenu === 'home') {
      navigate(nextPath ?? IYAGIGO_PATH.HOME_BEGIN_PATH);
      return;
    } else if (mainMenu === 'cloze') {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu, subMenu: subMenu ?? null }));
      navigate(nextPath ?? createClozePath('home'));
      return;
    } else if (mainMenu === 'test') {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu, subMenu: subMenu ?? null }));
      navigate(nextPath ?? createTestPath('home'));
    } else if (mainMenu === 'classroom') {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu, subMenu: subMenu ?? null }));
      navigate(nextPath ?? createClassroomPath('home'));
    } else if (mainMenu === 'clip') {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu, subMenu: subMenu ?? null }));
      navigate(nextPath ?? createClipPath('home'));
    } else if (mainMenu === 'hub') {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu, subMenu: subMenu ?? null }));
      navigate(nextPath ?? createHubPath('home'));
    } else if (mainMenu === 'studio') {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu, subMenu: subMenu ?? null }));
      navigate(nextPath ?? createStudioPath('home'));
    } else if (mainMenu === 'vocabulary-book') {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu, subMenu: subMenu ?? null }));
      navigate(nextPath ?? createVocabularyBookPath('home'));
    } else if (mainMenu === 'speaking') {
      dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu, subMenu: subMenu ?? null }));
      navigate(nextPath ?? createSpeakingPath());
    }
  };

  const handleContinueClick = () => {
    dispatch(modalActions.hideModal());
  };

  return (
    <Wrapper>
      <TextBlock text={'학습을 종료할까요?'} design={DIALOG_TEXT_DESIGN} />
      <Gap height={size.MARGIN_2} />
      <ButtonContainer>
        <TextButton text='그만하기' theme={ETheme.GRAY} size='medium' shape='pill' width='100%' handleClick={handleQuitClick} />
        <TextButton text='이어서하기' theme={ETheme.BROWN} size='medium' shape='pill' width='100%' handleClick={handleContinueClick} />
      </ButtonContainer>
    </Wrapper>
  );
};

export default WorkbookQuitModal;
